import { useMemo, useState } from 'react';
import {
  type CreateReagentLotInput,
  type Department,
  type ReagentItemStock,
  type Supplier,
  createReagentLotSchema,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { Page } from '#client/components/ui/Page';
import { useFormSubmit } from '#client/hooks/useFormSubmit';
import { useFormValues } from '#client/hooks/useFormValues';
import { RESOURCE, routes } from '#client/lib/routes';
import { BenchCard, DeliveryCard, ReagentCard } from './StockEntryFields';
import { EMPTY_LOT_ENTRY, nextLotEntry } from './stock-entry.logic';
import { enterStock } from './stock.service';
import { ButtonLink } from '#client/components/ui/ButtonLink';
import { useAllRecords } from '#client/hooks/useAllRecords';

/**
 * Entering stock — the lab's "New Entry" screen.
 *
 * Their spreadsheet made this nine separate sheets ("Enter Serology Items",
 * "Enter Clinical Chemistry Items", …). That is a launcher, not nine different
 * jobs: the form is identical every time, and only the list of reagents
 * changes. So the bench is a filter here rather than a screen, which also means
 * adding a tenth department costs nothing.
 *
 * Posts an `opening_balance` movement through the same ledger the deduction
 * uses, so day-one counts and every later correction reconcile the same way.
 */
export function StockEntryPage() {
  const [departmentId, setDepartmentId] = useState('');

  const departments = useAllRecords<Department>({ path: RESOURCE.departments });
  const suppliers = useAllRecords<Supplier>({ path: RESOURCE.suppliers });

  const extraQuery = useMemo(
    () => ({ active: 'true', ...(departmentId ? { department_id: departmentId } : {}) }),
    [departmentId],
  );
  const items = useAllRecords<ReagentItemStock>({
    path: RESOURCE.reagentItems,
    extraQuery,
  });

  const { values, set, setValues } = useFormValues<CreateReagentLotInput>(EMPTY_LOT_ENTRY);

  const form = useFormSubmit<CreateReagentLotInput>({
    schema: createReagentLotSchema,
    submit: enterStock,
    successMessage: 'Stock entered',
    onSuccess: () => {
      // The reagent list is re-read so the new on-hand shows immediately, and
      // the form resets to the same item.
      items.refresh();
      setValues(nextLotEntry);
    },
  });

  const selected = items.items.find((item) => item.id === values.item_id);

  function handleBenchChange(nextDepartmentId: string) {
    setDepartmentId(nextDepartmentId);
    // The chosen reagent may not be on the new bench.
    set('item_id', 0);
  }

  return (
    <Page
      title="Enter stock"
      subtitle="Record what has arrived, or what is already on the shelf"
      backAction={
        <ButtonLink variant="secondary" size="md" to={routes.reagentItems}>
          Back to inventory
        </ButtonLink>
      }
    >
      <form onSubmit={form.handleSubmit(values)} noValidate>
        <div className="c-form-stack">
          {form.formError && <Banner tone="critical">{form.formError}</Banner>}

          <BenchCard
            departmentId={departmentId}
            departments={departments.items}
            onBenchChange={handleBenchChange}
          />

          <ReagentCard
            values={values}
            onFieldChange={set}
            errors={form.errors}
            items={items.items}
            selected={selected}
          />

          <DeliveryCard
            values={values}
            onFieldChange={set}
            errors={form.errors}
            suppliers={suppliers.items}
            selected={selected}
          />

          <FormActions
            cancelTo={routes.reagentItems}
            submitLabel="Enter stock"
            submitting={form.submitting}
          />
        </div>
      </form>
    </Page>
  );
}
